/**
 * Logger utility for consistent logging across the application
 * Automatically suppresses non-critical logs in production
 */

// Environment check
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Log information only in development environment
 * @param message The message to log
 * @param data Optional data to include with the log
 */
export const log = (message: string, data?: any): void => {
  if (isDevelopment) {
    if (data !== undefined) {
      console.log(message, data);
    } else {
      console.log(message);
    }
  }
};

/**
 * Log warnings only in development environment
 * @param message The warning message
 * @param data Optional data to include with the warning
 */
export const warn = (message: string, data?: any): void => {
  if (isDevelopment) {
    if (data !== undefined) {
      console.warn(message, data);
    } else {
      console.warn(message);
    }
  }
};

/**
 * Log errors in all environments (development and production)
 * @param message The error message
 * @param data Optional error object or additional data
 */
export const error = (message: string, data?: any): void => {
  if (data !== undefined) {
    console.error(message, data);
  } else {
    console.error(message);
  }
};

/**
 * Log information that should always be visible, even in production
 * Use sparingly for critical information only
 * @param message The message to log
 * @param data Optional data to include with the log
 */
export const critical = (message: string, data?: any): void => {
  if (data !== undefined) {
    console.log(`[CRITICAL] ${message}`, data);
  } else {
    console.log(`[CRITICAL] ${message}`);
  }
};

/**
 * Log info information similar to log but with info level
 * @param message The info message
 * @param data Optional data to include with the info log
 */
export const info = (message: string, data?: any): void => {
  if (isDevelopment) {
    if (data !== undefined) {
      console.info(message, data);
    } else {
      console.info(message);
    }
  }
};

/**
 * Log debug information (most verbose, only in development)
 * @param message The debug message
 * @param data Optional data to include with the debug log
 */
export const debug = (message: string, data?: any): void => {
  if (isDevelopment) {
    if (data !== undefined) {
      console.debug(message, data);
    } else {
      console.debug(message);
    }
  }
};

// Default export for easier imports
const logger = {
  log,
  warn,
  error,
  critical,
  info,
  debug
};

export default logger; 